import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, CheckCircle2, Coins, Cpu, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { trainingApi, type TrainingCampaignDetail, type TrainingPipelineNode } from '../services/api';
import Waiting, { StatusPill } from '../components/training/Waiting';
import { useAppStore } from '../store/useAppStore';
import { CheckoutBadge } from '../components/workitems/CheckoutBadge';

/**
 * One campaign (EP-26 FE-5 ST-5): its batches and rounds as a dependency graph,
 * the gate each waits on, its runs, gate history and spend. Item states come from
 * the work items; history and spend are read from the programme's stores, and a
 * store that cannot be read is stated rather than shown as empty.
 */
const COL_W = 190;
const ROW_H = 92;
const BOX_W = 160;
const BOX_H = 56;

function boxClass(n: TrainingPipelineNode) {
  if (n.waiting.kind === 'gate') return { stroke: '#dc2626', fill: '#fef2f2', dash: '4 3' };      // blocked
  if (n.waiting.kind === 'done') return { stroke: '#16a34a', fill: '#f0fdf4', dash: '' };
  if (n.waiting.kind === 'approval' || n.waiting.kind === 'pending_approval') return { stroke: '#d97706', fill: '#fffbeb', dash: '' };
  if (n.waiting.kind === 'cancelled') return { stroke: '#9ca3af', fill: '#f9fafb', dash: '' };
  return { stroke: '#3b82f6', fill: '#eff6ff', dash: '' };
}

function Graph({ batches, rounds, onPick, picked }: {
  batches: TrainingPipelineNode[]; rounds: TrainingPipelineNode[];
  onPick: (id: string) => void; picked: string;
}) {
  const nodes = [...batches, ...rounds];
  const pos = new Map<string, { x: number; y: number }>();
  batches.forEach((n) => pos.set(n.id, { x: 10 + (n.index - 1) * COL_W, y: 10 }));
  rounds.forEach((n) => pos.set(n.id, { x: 10 + (n.index - 1) * COL_W, y: 10 + ROW_H }));
  const cols = Math.max(batches.length, rounds.length, 1);
  const width = 20 + cols * COL_W;
  const height = 20 + 2 * ROW_H - (ROW_H - BOX_H);
  const edges = nodes.flatMap((n) => n.dependencies
    .filter((d) => pos.has(d.id))
    .map((d) => ({ from: pos.get(d.id)!, to: pos.get(n.id)!, done: d.done, key: `${d.id}>${n.id}` })));

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
      <svg width={width} height={height} role="img" aria-label="Campaign dependency graph">
        <defs>
          <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#64748b" />
          </marker>
        </defs>
        {edges.map((e) => {
          const sameRow = e.from.y === e.to.y;
          const x1 = sameRow ? e.from.x + BOX_W : e.from.x + BOX_W / 2;
          const y1 = sameRow ? e.from.y + BOX_H / 2 : e.from.y + BOX_H;
          const x2 = sameRow ? e.to.x : e.to.x + BOX_W / 2;
          const y2 = sameRow ? e.to.y + BOX_H / 2 : e.to.y;
          return <line key={e.key} x1={x1} y1={y1} x2={x2} y2={y2} stroke={e.done ? '#16a34a' : '#64748b'}
            strokeWidth={1.5} strokeDasharray={e.done ? '' : '5 4'} markerEnd="url(#arrow)" />;
        })}
        {nodes.map((n) => {
          const p = pos.get(n.id)!;
          const s = boxClass(n);
          return (
            <g key={n.id} transform={`translate(${p.x},${p.y})`} onClick={() => onPick(n.id)} style={{ cursor: 'pointer' }}>
              <title>{`${n.title}\n${n.statusLabel}${n.waiting.reason ? `\n${n.waiting.reason}` : ''}\n${n.checkout ? `Checked out by ${n.checkout.holder} until ${new Date(n.checkout.until).toLocaleString()}` : 'Not being worked'}`}</title>
              <rect width={BOX_W} height={BOX_H} rx={8} fill={s.fill} stroke={s.stroke}
                strokeWidth={picked === n.id ? 3 : 1.5} strokeDasharray={s.dash} />
              <text x={10} y={22} fontSize={13} fontWeight={600} fill="#111827">
                {n.kind === 'batch' ? 'Batch' : 'Round'} {n.index}
              </text>
              <text x={10} y={42} fontSize={11} fill="#4b5563">
                {n.statusLabel}{n.waiting.kind === 'gate' ? ' · blocked' : ''}
              </text>
              {n.checkout && (
                <text x={BOX_W - 8} y={22} fontSize={10} textAnchor="end" fill="#6d28d9">
                  {'held: ' + String(n.checkout.holder).split('@')[0].slice(0, 12)}
                </text>)}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export default function TrainingCampaignPage() {
  const { programId = '', campaignId = '' } = useParams<{ programId: string; campaignId: string }>();
  const [data, setData] = useState<TrainingCampaignDetail | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState('');
  const [plan, setPlan] = useState<any>(null);
  const [matrix, setMatrix] = useState<any>(null);
  const openItem = useAppStore((st) => st.setDetailItemId);

  const load = useCallback(() => {
    setLoading(true);
    trainingApi.campaign(programId, campaignId)
      .then((d) => { setData(d); setError(''); })
      .catch((e) => setError(String(e?.message || e)))
      .finally(() => setLoading(false));
    trainingApi.campaignPlan(programId, campaignId).then(setPlan).catch(() => setPlan(null));
    trainingApi.campaignRounds(programId, campaignId).then(setMatrix).catch(() => setMatrix(null));
  }, [programId, campaignId]);

  useEffect(() => { load(); }, [load]);

  const nodes = useMemo(() => (data ? [...data.batches, ...data.rounds] : []), [data]);
  const selected = nodes.find((n) => n.id === picked) || null;

  if (error) return <div className="p-6 text-sm text-red-600">{error}</div>;
  if (!data) return <div className="p-6 flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading campaign…</div>;

  const history = selected ? data.gateHistory[selected.id] || [] : [];
  const runs = data.runs.filter((r) => !selected || r.parentId === selected.id
    || (data.activities[selected.id] || []).some((a) => a.id === r.parentId));

  return (
    <div className="p-6 space-y-5">
      <header className="space-y-1">
        <Link to={`/training/${encodeURIComponent(programId)}`} className="text-xs text-gray-500 hover:text-accent flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Pipeline
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            <span className="text-sm text-gray-500 mr-2">{data.key}</span>{data.title}
          </h1>
          <StatusPill label={data.statusLabel} />
          <button type="button" onClick={load} className="text-xs flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-accent">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
        <Waiting waiting={data.waiting} />
        <div className="text-xs text-gray-500">
          {Object.entries(data.pins).filter(([, v]) => v).map(([k, v]) => <span key={k} className="mr-3">{k}: <code>{v}</code></span>)}
        </div>
      </header>

      {data.storeState !== 'ok' && (
        <div className="text-xs text-amber-700 dark:text-amber-400 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Gate history and spend are incomplete: {data.messages.join(' ') || data.storeState}</span>
        </div>
      )}

      <section className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500 flex items-center gap-1"><Coins className="w-3.5 h-3.5" /> Spend</div>
          <div className="mt-1 text-lg font-medium text-gray-900 dark:text-gray-100 tabular-nums">${data.spend.total.toFixed(2)}</div>
          <div className="text-xs text-gray-500">{data.spend.counted} run(s){data.spend.missing ? `, ${data.spend.missing} unread` : ''}</div>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500 flex items-center gap-1"><Cpu className="w-3.5 h-3.5" /> Runs</div>
          <div className="mt-1 text-lg font-medium text-gray-900 dark:text-gray-100 tabular-nums">{data.runs.length}</div>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500">Blocked</div>
          <div className="mt-1 text-lg font-medium text-gray-900 dark:text-gray-100 tabular-nums">
            {nodes.filter((n) => n.waiting.kind === 'gate').length} of {nodes.length}
          </div>
        </div>
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Dependencies</h2>
        <Graph batches={data.batches} rounds={data.rounds} onPick={(id) => setPicked(id === picked ? '' : id)} picked={picked} />
        <p className="text-xs text-gray-500">Dashed red: blocked by a gate. Dashed arrow: dependency not finished. Select a batch or round for its detail.</p>
      </section>

      {selected && (
        <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              <span className="text-xs text-gray-500 mr-1">{selected.key}</span>{selected.title}
            </h2>
            <StatusPill label={selected.statusLabel} color={selected.color} />
            <CheckoutBadge checkout={selected.checkout} />
            <button type="button" onClick={() => openItem(selected.id)}
              className="ml-auto px-2 py-0.5 text-xs rounded border border-gray-300 dark:border-gray-600 hover:border-accent">
              Open: move, assign, check out</button>
          </div>
          <Waiting waiting={selected.waiting} />
          {selected.dependencies.length > 0 && (
            <div className="text-xs text-gray-600 dark:text-gray-400">
              Waits for: {selected.dependencies.map((d) => `${d.key || d.id} (${d.status || '—'}${d.done ? '' : d.checkout ? `, checked out by ${d.checkout.holder}` : ', not being worked'})`).join(', ')}
            </div>
          )}
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Activities</div>
            <ul className="text-xs space-y-0.5">
              {(data.activities[selected.id] || []).map((a) => (
                <li key={a.id} className="text-gray-800 dark:text-gray-200">
                  <span className="text-gray-500 mr-1">{a.key}</span>{a.title} · {a.status}{a.manual ? ' · manual' : ''}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Gate history</div>
            {history.length === 0 ? <p className="text-xs text-gray-500">No gate events recorded.</p> : (
              <ol className="space-y-1">
                {history.map((g) => (
                  <li key={g.id} className="text-xs text-gray-800 dark:text-gray-200 flex items-start gap-1.5">
                    {g.passed ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                      : <XCircle className="w-3.5 h-3.5 text-red-600 mt-0.5 shrink-0" />}
                    <span>
                      <strong>{g.gate}</strong> {g.passed ? 'passed' : 'refused'}
                      <span className="text-gray-500"> · {g.at ? new Date(g.at).toLocaleString() : '—'}{g.actor ? ` · ${g.actor}` : ''}</span>
                      {g.reason ? <span className="block text-gray-600 dark:text-gray-400">{g.reason}</span> : null}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </section>
      )}

      {plan && plan.rounds?.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Round plan</h2>
          {!plan.order?.ok && (
            <p className="text-xs text-red-600">Batches cannot be trained in order: {[...(plan.order.outOfOrder || []), ...(plan.order.cycles || []).map((c: string[]) => 'cycle ' + c.join(' → '))].join('; ')}</p>)}
          <p className="text-xs text-gray-500">Each round trains once on every batch so far. Refresh: {plan.refreshBasis}.
            {plan.cost?.state === 'ok'
              ? ` Cost from ${plan.cost.reference} (${plan.cost.baseModel} on ${plan.cost.gpuCount}× ${plan.cost.gpu}, ${plan.cost.rowsPerGpuHour} rows/GPU-h${plan.cost.price ? `, ${plan.cost.price}` : ''}).`
              : ` ${plan.cost?.message || ''}`}</p>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase text-gray-500">
                <tr><th className="text-left p-2">Round</th><th className="text-right p-2">New</th><th className="text-right p-2">Refresh</th>
                  <th className="text-right p-2">Units</th><th className="text-right p-2">Rows (unique / trained)</th>
                  <th className="text-right p-2">GPU-h</th><th className="text-right p-2">Est. cost</th></tr>
              </thead>
              <tbody>
                {plan.rounds.map((r: any) => (
                  <tr key={r.round} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="p-2">{r.roundItem ? <button type="button" className="hover:text-accent" onClick={() => openItem(r.roundItem.id)}>Round {r.round}</button> : `Round ${r.round}`}
                      <span className="text-xs text-gray-500 ml-1">{r.roundItem?.status || ''}</span></td>
                    <td className="p-2 text-right tabular-nums" title={r.newUnits.join(', ')}>{r.newUnits.length}</td>
                    <td className="p-2 text-right tabular-nums" title={r.refreshUnits.join(', ')}>{r.refreshUnits.length}</td>
                    <td className="p-2 text-right tabular-nums">{r.cumulativeUnits}</td>
                    <td className="p-2 text-right tabular-nums">{r.uniqueRows.toLocaleString()} / {r.rowsConsumed.toLocaleString()}</td>
                    <td className="p-2 text-right tabular-nums">{r.gpuHours ?? '—'}</td>
                    <td className="p-2 text-right tabular-nums">{r.costUsd != null ? `$${r.costUsd.toFixed(2)}` : '—'}</td>
                  </tr>))}
              </tbody>
            </table>
          </div>
        </section>)}

      {matrix && matrix.rounds?.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Progress across rounds</h2>
          <p className="text-xs text-gray-500">Score / unsafe per unit at its worst seed. Red: regressed against the unit's last accepted round
            (score drop over {matrix.tolerance.mainScoreDrop}, unsafe rise over {matrix.tolerance.unsafeRise}). Bold: new in that round.</p>
          {matrix.storeError && <p className="text-xs text-amber-700 dark:text-amber-400">Results are incomplete: {matrix.storeError}</p>}
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-xs text-gray-500">
                <tr><th className="text-left p-2 uppercase">Unit</th>
                  {matrix.rounds.map((c: any) => (
                    <th key={c.id} className="text-right p-2">
                      <button type="button" className="hover:text-accent" onClick={() => openItem(c.id)}>
                        Round {c.round}{c.champion ? ' ★' : ''}</button>
                      <span className="block font-normal">{c.status}{c.state !== 'ok' ? ` · ${c.state === 'missing' ? 'results missing' : 'no results'}` : ''}</span>
                    </th>))}
                </tr>
              </thead>
              <tbody>
                {matrix.units.length === 0 && (
                  <tr><td className="p-2 text-xs text-gray-500" colSpan={matrix.rounds.length + 1}>No unit results recorded yet.</td></tr>)}
                {matrix.units.map((row: any) => (
                  <tr key={row.unit} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="p-2 text-gray-900 dark:text-gray-100"><code>{row.unit}</code></td>
                    {row.cells.map((x: any) => (
                      <td key={x.round} title={x.regression || ''}
                        className={`p-2 text-right tabular-nums ${x.regression ? 'text-red-600 bg-red-50 dark:bg-red-900/20' : 'text-gray-700 dark:text-gray-300'} ${x.new ? 'font-semibold' : ''}`}>
                        {x.mainScore != null ? `${Number(x.mainScore).toFixed(3)} / ${x.unsafe ?? 0}` : '—'}
                      </td>))}
                  </tr>))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500">★ the serving champion's round.</p>
        </section>)}

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Runs{selected ? ` · ${selected.title}` : ''}</h2>
        {runs.length === 0 ? <p className="text-xs text-gray-500">No runs.</p> : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase text-gray-500">
                <tr><th className="text-left p-2">Run</th><th className="text-left p-2">Stage</th><th className="text-left p-2">Status</th><th className="text-right p-2">Cost</th></tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
                    onClick={() => openItem(r.id)} title="Open the run">
                    <td className="p-2 text-gray-900 dark:text-gray-100"><span className="text-xs text-gray-500 mr-1">{r.key}</span>{r.title}</td>
                    <td className="p-2 text-gray-700 dark:text-gray-300">{r.stage || '—'}</td>
                    <td className="p-2 text-gray-700 dark:text-gray-300">{r.status}</td>
                    <td className="p-2 text-right tabular-nums text-gray-900 dark:text-gray-100">{r.cost != null ? `$${r.cost.toFixed(2)}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
